import cn from 'classnames';
import { useEffect, useState } from 'react';
import LdsIcon from '../LdsIcon/index.jsx';

// JSDoc comments to supply typscript compiler with the necessary types
/**
 * @typedef LdsAccordionProps
 * @property {string} accordionId - ID to assign to accordion. Required for accordion to work.
 * @property {boolean} [open] - When this attribute is present, the accordion will be in the open expanded state. This attribute takes no value but acts as boolean "true" when present.
 * @property {string=} [className] - Optional custom user-defined CSS class names, will be appended to the list of any other component classes
 * @property {Array.<React.ReactNode>} [children] - Optional children
 */

/**
 * LdsAccordion component
 * @param {LdsAccordionProps & React.InputHTMLAttributes<HTMLInputElement>} props
 * @returns {React.JSX.Element} - LdsAccordion component
 */
function LdsAccordion({
  accordionId,
  open = undefined,
  className,
  children,
  ...rest
}) {
  return (
    <div
      id={accordionId}
      className={cn(
        'lds-accordion',
        {
          open,
        },
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
// JSDoc comments to supply typscript compiler with the necessary types
/**
 * @typedef LdsAccordionHeaderProps
 * @property {string} accordionId - ID to assign to accordion. Required for accordion to work.
 * @property {boolean} [open] - When this attribute is present, the accordion will be in the open expanded state. This attribute takes no value but acts as boolean "true" when present.
 * @property {string} iconOpenLabel - Label or action text value for the icon used when accordion is open/expand.
 * @property {string} iconClosedLabel - Label or action for the icon used when accordion is close/collapse.
 * @property {string} iconOpenName - Valid Icon value for the icon used when accordion is open/expand.
 * @property {string} iconClosedName - Valid Icon value for the icon used when accordion is close/collapse.
 * @property {Array.<React.ReactNode>|React.ReactNode} [children] - Optional children
*/
/**
 * LdsAccordion.Header subcomponent
 * @param {LdsAccordionHeaderProps} props
 * @returns {React.JSX.Element} - LdsAccordionHeader component
 */
function Header({
  accordionId,
  open,
  iconOpenLabel = 'Collapse accordion',
  iconClosedLabel = 'Expand accordion',
  iconOpenName = 'caret-up',
  iconClosedName = 'caret-down',
  children,
}) {
  const [isOpen, setIsOpen] = useState(open);

  const setAccordionOpenHeight = () => {
    if (!accordionId) return;

    const accordion = document.getElementById(accordionId);
    const accordionBody = document.querySelector(`#${accordionId} .lds-accordion-body`);

    if (isOpen) {
      accordion.classList.add('open');
      accordionBody.style.maxHeight = `${accordionBody.scrollHeight}px`;
      accordionBody.style.visibility = 'visible';
      accordionBody.setAttribute('aria-hidden', 'false');
    } else {
      accordion.classList.remove('open');
      accordionBody.style.maxHeight = 0;
      accordionBody.style.visibility = 'hidden';
      accordionBody.setAttribute('aria-hidden', 'true');
    }
  };

  useEffect(() => {
    setAccordionOpenHeight();
  });

  const handleToggle = () => {
    setIsOpen(!isOpen);
    setAccordionOpenHeight();
  };

  return (
    <div className="lds-accordion-header" id={`${accordionId}-heading`}>
      <div className={`lds-accordion-toggle ${isOpen ? 'open' : ''}`}>
        <button
          onClick={() => handleToggle()}
          className="lds-button-clear-style toggle-btn"
          aria-expanded={isOpen ? 'true' : 'false'}
          aria-controls={`${accordionId}-body`}
        >
          {children}
          <LdsIcon
            className="lds-accordion-toggle-icon"
            label={`${isOpen ? iconOpenLabel : iconClosedLabel}`}
            name={`${isOpen ? iconOpenName : iconClosedName}`}
          />
        </button>
      </div>
    </div>
  );
}
LdsAccordion.Header = Header;

// JSDoc comments to supply typscript compiler with the necessary types
/**
 * @typedef LdsAccordionBodyProps
 * @property {string} accordionId - ID to assign to accordion. Required for accordion to work.
 * @property {React.ReactNode} [children] - Optional children
*/
/**
 * LdsAccordion.Body subcomponent
 * @param {LdsAccordionBodyProps} props
 * @returns {React.JSX.Element} - LdsAccordionBody component
 */
function Body({ accordionId, children }) {
  return (
    <div className="lds-accordion-body" id={`${accordionId}-body`} role="region" aria-labelledby={`${accordionId}-heading`}>
      <div className="lds-accordion-panel">
        {children}
      </div>
    </div>
  );
}
LdsAccordion.Body = Body;

export default LdsAccordion;
